import React, { memo, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { createSelector } from 'reselect';
import { useInView } from 'react-intersection-observer';
import { debounce } from 'lodash-es';

import styles from './GameInteract.module.less';
import Interact from '../../components/Game/Interact/Interact';
import SliderTag from '../../components/Game/SliderTag/SliderTag';
import { createAxios, getSoftType } from '../../utils/utils';
import { CONTEXT, GAME_EXT_SCREEN_SEARCH_86 } from '../../config';
import { logging, logPostInfo } from '../../actions/log';

const selectLogInfo = createSelector(
  state => state.log,
  log => log.logInfo,
);
const selectwidowSize = createSelector(
  state => state.layout,
  layout => layout.widowSize,
);

export default memo(({ data, logInfo }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const logInfoData = useSelector(selectLogInfo);
  const windowSize = useSelector(selectwidowSize);

  const [aPageSize, setAPageSize] = useState(5); // 当前一页展示的应用数量
  const [aShowOnePage, setAShowOnePage] = useState({
    showStart: 0,
    showEnd: 5,
  }); // 当前展示那些应用的游标
  const [isInChange, setIsInChange] = useState(false); // 主要为了处理，翻页时，展现数据不上报的问题

  const [allTag, setAllTag] = useState(null);
  const [activeTag, setActiveTag] = useState(null);
  const [isNeedUpdate, setIsNeedUpdate] = useState(false);
  const [isLoading, setIsLoading] = useState(0);

  const [allSofts, setAllSofts] = useState([]);

  const [slider1, setSlider1] = useState(100);
  const [slider2, setSlider2] = useState(100);
  const [slider3, setSlider3] = useState(100);

  const LOG_ACTION_SOURCE = {
    c_action: `${logInfo.clTag || logInfo.tag}_Interact_Screen`,
    c_change: `${logInfo.clTag || logInfo.tag}_Group_ChangePage`,
    c_progress: `${logInfo.clTag || logInfo.tag}_Interact_Progress`,
    source: `${logInfo.tag}_Group`,
  };

  const [refInView, inView] = useInView({
    threshold: 0,
  });
  const [refInView75, inView75] = useInView({
    threshold: 0.75,
  });

  const changeTageLoggingCardPV = useCallback(pageCCDate => {
    if (pageCCDate && pageCCDate.code) {
      logInfo.changView({
        ...logInfo,
        pageCardId: pageCCDate.pageCardContentId,
        pageCardTitle: data && data.title,
        rankCode: pageCCDate.code,
        cardType: data && data.cardType,
        org: pageCCDate.org || '',
      });
    }
  }, []);

  useEffect(() => {
    if (inView75 && logInfo && logInfo.changView) {
      changeTageLoggingCardPV(data?.dataList[0]);
    }
  }, [inView75]);

  // 设置选择条件
  useEffect(() => {
    if (data && data.dataList && data.dataList.length > 0) {
      setAllTag(data.dataList);
      setActiveTag(data.dataList[0]);
      if (data.dataList[0] && data.dataList[0].apps) {
        setAllSofts(data.dataList[0].apps || []);
      }
    }
  }, [data]);

  // 处理拉伸
  useEffect(() => {
    if (allSofts?.length > 0 && windowSize?.width) {
      let pageSize = calculationPageSize();
      setAPageSize(size => {
        if (size !== pageSize) {
          return pageSize;
        }
        return size;
      });
      setAShowOnePage(onePage => {
        let showOnePage = {
          showStart: onePage.showStart,
          showEnd: onePage.showStart + pageSize,
        };
        if (showOnePage.showEnd > allSofts.length) {
          showOnePage = {
            showStart: allSofts.length - pageSize,
            showEnd: allSofts.length,
          };
        }

        if (showOnePage.showStart < 0) {
          showOnePage.showStart = 0;
        }
        if (showOnePage.showEnd > allSofts.length) {
          showOnePage.showEnd = allSofts.length;
        }
        return showOnePage;
      });
    }
  }, [allSofts, windowSize]);

  // 根据窗口 宽度 计算需要显示的应用个数
  const calculationPageSize = useCallback(() => {
    let windowWidth = document.body.clientWidth || 1060;
    windowWidth = windowWidth > 1960 ? 1960 : windowWidth < 1060 ? 1060 : windowWidth;
    // 计算一行的个数 256n + 16(n-1) = 总宽度，所以 n = (总宽度 + 16) / 272
    let diff = 16;
    const onePageSize = parseInt((windowWidth - 260 - 348 + 16) / (210 + diff)) * 2 + 1;
    return onePageSize;
  }, [windowSize]);

  const handleChangeSoft = useCallback(
    (point, flag) => {
      if (allSofts && allSofts.length === 0) {
        return [];
      }
      let showOnePage = { ...aShowOnePage };

      if (point === 'next') {
        // 处理 aShowOnePage
        showOnePage.showStart = aShowOnePage.showStart + aPageSize; // 下一页开始
        showOnePage.showEnd = aShowOnePage.showEnd + aPageSize; // 下一页结束
        if (showOnePage.showEnd > allSofts.length) {
          showOnePage.showStart = allSofts.length - aPageSize;
          showOnePage.showEnd = allSofts.length;
        }
      }
      if (point === 'prev') {
        // 处理 aShowOnePage
        showOnePage.showStart = aShowOnePage.showStart - aPageSize; // 上一页开始
        showOnePage.showEnd = aShowOnePage.showEnd - aPageSize; // 上一页结束
        if (showOnePage.showStart < 0) {
          showOnePage.showStart = 0;
          showOnePage.showEnd = aPageSize;
        }
      }
      // 处理异常
      if (showOnePage.showStart < 0) {
        showOnePage.showStart = 0;
      }
      if (showOnePage.showEnd > allSofts.length) {
        showOnePage.showEnd = allSofts.length;
      }

      setIsInChange(true);
      let index = setTimeout(() => {
        setAShowOnePage(showOnePage);
        setIsInChange(false);
        clearTimeout(index);
      }, 100);

      // 点击换一换, flag第一次切换不上报
      dispatch(
        logging({
          category: 'C',
          action: LOG_ACTION_SOURCE.c_change,
          label: '',
          param2: {
            ...logInfo,
            source: `${LOG_ACTION_SOURCE.source}||${activeTag.pageCardContentId}`,
            source_p: logInfoData.source_p,
            source_pp: logInfoData.source_pp,
            pageCardContentId: activeTag.pageCardContentId,
            nextOrPrev: point,
            title: activeTag.name,
            // pageNum: getPageNum(showOnePage) || 1,
            // pageSize: aPageSize,
            // ...aShowOnePage,
          },
        }),
      );
    },
    [dispatch, logInfoData, activeTag, windowSize, allSofts, aPageSize, aShowOnePage],
  );

  // const getPageNum = useCallback(showOnePage => {
  //   let page = 1;
  //   let pageSize = calculationPageSize();
  //   if (showOnePage?.showEnd) {
  //     page = parseInt(showOnePage?.showEnd / pageSize);
  //     if (showOnePage?.showEnd % pageSize > 0) {
  //       page = page + 1;
  //     }
  //   }
  //   return page;
  // }, []);

  const getInteractData = useCallback((tag, s1, s2, s3) => {
    setIsLoading(1);
    setAllSofts([]);
    createAxios().then(axios => {
      axios
        .post(GAME_EXT_SCREEN_SEARCH_86, {
          interactType: tag.interactType,
          code: tag.code,
          updateTimeSlider: s1 / 100,
          scoreRadioSlider: s2 / 100,
          hotSlider: s3 / 100,
        })
        .then(res => {
          if (res?.data && res?.data?.status === 0) {
            setAllSofts(res?.data?.data || []);
            setAShowOnePage({
              showStart: 0,
              showEnd: calculationPageSize(),
            });
            setIsLoading(0);
          } else {
            setIsLoading(2);
          }
        })
        .catch(() => {
          setIsLoading(2);
        });
    });
  }, []);

  useEffect(() => {
    if (isNeedUpdate) {
      getInteractData(activeTag, slider1, slider2, slider3);
      setIsNeedUpdate(false);
    }
  }, [slider1, slider2, slider3, activeTag, isNeedUpdate]);

  const handleChangeTag = useCallback(
    tag => {
      if (activeTag.code === tag.code) {
        return;
      }
      setActiveTag(tag);
      setIsNeedUpdate(+new Date());
      dispatch(
        logging({
          category: 'C',
          action: `${logInfo.clTag || logInfo.tag}_Interact_Screen`,
          label: '',
          param2: {
            ...logInfo,
            source: `${LOG_ACTION_SOURCE.source}||${activeTag.pageCardContentId}`,
            source_p: logInfoData.source_p,
            source_pp: logInfoData.source_pp,
            pageCardContentId: activeTag.pageCardContentId,
            name: tag.name,
            interactType: tag?.interactType, // 1:分类   2:标签   3;自定义标签
            code: tag?.code,
          },
        }),
      );
      changeTageLoggingCardPV(tag);
    },
    [slider1, slider2, slider3, activeTag],
  );
  const onChange1 = useCallback(
    value => {
      setSlider1(value);
      setIsNeedUpdate(+new Date());
      dispatch(
        logging({
          category: 'C',
          action: `${logInfo.clTag || logInfo.tag}_Interact_Progress`,
          label: '',
          param2: {
            ...logInfo,
            source: `${LOG_ACTION_SOURCE.source}||${activeTag.pageCardContentId}`,
            source_p: logInfoData.source_p,
            source_pp: logInfoData.source_pp,
            pageCardContentId: activeTag.pageCardContentId,
            type: 'update',
            value: value / 100,
          },
        }),
      );
    },
    [slider1, slider2, slider3, activeTag],
  );
  const onChange2 = useCallback(
    value => {
      setSlider2(value);
      setIsNeedUpdate(+new Date());
      dispatch(
        logging({
          category: 'C',
          action: `${logInfo.clTag || logInfo.tag}_Interact_Progress`,
          label: '',
          param2: {
            ...logInfo,
            source: `${LOG_ACTION_SOURCE.source}||${activeTag.pageCardContentId}`,
            source_p: logInfoData.source_p,
            source_pp: logInfoData.source_pp,
            pageCardContentId: activeTag.pageCardContentId,
            type: 'praise',
            value: value / 100,
          },
        }),
      );
    },
    [slider1, slider2, slider3, activeTag],
  );
  const onChange3 = useCallback(
    value => {
      setSlider3(value);
      setIsNeedUpdate(+new Date());
      dispatch(
        logging({
          category: 'C',
          action: `${logInfo.clTag || logInfo.tag}_Interact_Progress`,
          label: '',
          param2: {
            ...logInfo,
            source: `${LOG_ACTION_SOURCE.source}||${activeTag.pageCardContentId}`,
            source_p: logInfoData.source_p,
            source_pp: logInfoData.source_pp,
            pageCardContentId: activeTag.pageCardContentId,
            type: 'hot',
            value: value / 100,
          },
        }),
      );
    },
    [slider1, slider2, slider3, activeTag],
  );

  const fmtSlider1 = useCallback(value => {
    if (value <= 15) {
      return '远';
    } else if (15 < value && value <= 50) {
      return '较远';
    } else if (50 < value && value <= 85) {
      return '较近';
    } else if (85 < value && value <= 100) {
      return '近';
    }
  }, []);
  const fmtSlider2 = useCallback(value => {
    if (value <= 15) {
      return '低';
    } else if (15 < value && value <= 50) {
      return '较低';
    } else if (50 < value && value <= 85) {
      return '较高';
    } else if (85 < value && value <= 100) {
      return '高';
    }
  }, []);

  if (!(data?.dataList && activeTag && allTag)) {
    return '';
  }

  return (
    <div
      className={`${styles.card} ${inView ? styles.card_cardIn : styles.card_cardOut}`}
      data-item={data.cardType}
      ref={refInView}
    >
      {inView && (
        <div className={styles.card_box} ref={refInView75}>
          <>
            <div className={styles.card_header}>
              <div className={styles.card_header_title}>{data.title}</div>
              <div className={styles.header_change}>
                <div
                  className={`iconfont icon-game_p_l ${
                    aShowOnePage.showStart === 0 ? styles.header_change_dis : styles.header_change_
                  }`}
                  onClick={() => {
                    if (aShowOnePage.showStart !== 0) {
                      handleChangeSoft('prev');
                    }
                  }}
                ></div>
                <div
                  className={`iconfont icon-game_p_r ${
                    aShowOnePage.showEnd >= allSofts.length
                      ? styles.header_change_dis
                      : styles.header_change_
                  }`}
                  onClick={() => {
                    if (aShowOnePage.showEnd !== allSofts.length) {
                      handleChangeSoft('next');
                    }
                  }}
                ></div>
              </div>
            </div>
            <div className={styles.card_condition}>
              <div className={`condition_tags`}>
                {allTag?.map((item, i) => (
                  <div
                    className={`condition_tag ${item?.code === activeTag?.code &&
                      'condition_tag_active'}`}
                    key={i}
                    onClick={() => {
                      handleChangeTag(item);
                    }}
                    title={item?.name?.length > 4 ? item?.name : ''}
                  >
                    {item?.name || ''}
                  </div>
                ))}
              </div>
              <div className={`condition_line`}></div>
              <div className={`condition_sliders`}>
                <div className={`condition_item`}>
                  <div className={`condition_item_name`}>更新时间</div>
                  <div className={`condition_item_slider`}>
                    <SliderTag
                      param={{
                        formatterTip: fmtSlider1,
                        onTagChange: debounce(onChange1, 300),
                        defaultTip: '近',
                        defaultValue: 100,
                      }}
                    />
                  </div>
                </div>
                <div className={`condition_item`}>
                  <div className={`condition_item_name`}>热度</div>
                  <div className={`condition_item_slider`}>
                    <SliderTag
                      param={{
                        formatterTip: fmtSlider2,
                        onTagChange: debounce(onChange3, 300),
                        defaultTip: '高',
                        defaultValue: 100,
                      }}
                    />
                  </div>
                </div>
                <div className={`condition_item`}>
                  <div className={`condition_item_name`}>好评度</div>
                  <div className={`condition_item_slider`}>
                    <SliderTag
                      param={{
                        formatterTip: fmtSlider2,
                        onTagChange: debounce(onChange2, 300),
                        defaultTip: '高',
                        defaultValue: 100,
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className={styles.card_softs}>
              {(isInChange || isLoading === 1) && (
                <div className={styles.card_softs_loading}>
                  <div className={styles.card_softs_loading_bg}>
                    <div className={styles.card_softs_loading_img}></div>
                  </div>
                  <div className={styles.card_softs_loading_text}>加载中...</div>
                </div>
              )}
              {(isLoading === 2 || (isLoading === 0 && allSofts?.length === 0)) && (
                <div className={styles.card_softs_err}>
                  <div className={styles.card_softs_err_img}></div>
                  <div className={styles.card_softs_err_text}>找不到符合要求的结果</div>
                </div>
              )}
              {!isInChange && allSofts && allSofts.length > 0 && (
                <>
                  <div className={`${styles.card_softs_left} card_softs_left`}>
                    <Interact
                      soft={{ ...allSofts[aShowOnePage.showStart], card_show_style: 'big' }}
                      logInfo={{
                        ...logInfo,
                        source: `${LOG_ACTION_SOURCE.source}||${activeTag.pageCardContentId}`,
                        source_p: logInfoData.source_p,
                        source_pp: logInfoData.source_pp,
                        ///////////////////////////
                        softid: allSofts[aShowOnePage.showStart]?.softID,
                        softType: getSoftType(allSofts[0]),
                        contentType: 'soft',
                        rankCode: activeTag.code,
                        org: activeTag.org || '',
                        cardType: data.cardType,
                        cardName: data.cardType,
                        pageCardTitle: data.title,
                        pageCardId: activeTag.pageCardContentId,
                        ///////////////////////////
                      }}
                    />
                  </div>
                  <div className={`${styles.card_softs_right} card_softs_right`}>
                    {allSofts
                      .slice(aShowOnePage.showStart + 1, aShowOnePage.showEnd)
                      .map((item, j) => {
                        return (
                          <Interact
                            soft={{ ...item, card_show_style: 'small' }}
                            logInfo={{
                              ...logInfo,
                              source: `${LOG_ACTION_SOURCE.source}||${activeTag.pageCardContentId}`,
                              source_p: logInfoData.source_p,
                              source_pp: logInfoData.source_pp,
                              ///////////////////////////soft?.androidType === '1'
                              softid: item?.androidType === '1' ? item?.packageName : item?.softID,
                              softType: getSoftType(item),
                              contentType: 'soft',
                              rankCode: activeTag.code,
                              org: activeTag.org || '',
                              cardType: data.cardType,
                              cardName: data.cardType,
                              pageCardTitle: data.title,
                              pageCardId: activeTag.pageCardContentId,
                              ///////////////////////////
                            }}
                            key={j}
                          />
                        );
                      })}
                  </div>
                </>
              )}
            </div>
          </>
        </div>
      )}
    </div>
  );
});
